// Power utility functions matching webapp implementation

/**
 * Enhanced power extraction function from webapp
 * Extracts numeric kW value from various formats and sources
 */
export const extractKW = (val, chargerObj) => {
  // Direct numeric value
  if (typeof val === 'number' && val > 0) return val;
  
  // String with numeric value - enhanced regex to handle more formats
  if (typeof val === 'string') {
    
    // PRIORITY: Pure numeric strings (like webapp kilowatts "230")
    if (/^\d+(?:\.\d+)?$/.test(val.trim())) {
      const power = parseFloat(val);
      if (!isNaN(power) && power > 0 && power <= 500) {
        return power;
      }
    }
    
    // Remove common prefixes and clean the string
    const cleanVal = val
      .replace(/^(power|kw|kilowatt|max|output|charging)[:=\s]*/i, '')
      .trim();
    
    // ENHANCED: Try parentheses patterns first (for connector names like "Type 2 (230kW DC)")
    const parenthesesMatch = cleanVal.match(/\((\d+(?:\.\d+)?)\s*kw\s*(?:dc|ac)?\)/i);
    if (parenthesesMatch) {
      const power = parseFloat(parenthesesMatch[1]);
      if (power > 0 && power <= 500) return power; // Reasonable power range
    }
    
    // ENHANCED: Try DC/AC explicit patterns (like "50kW DC", "22kW AC", "230kW DC")
    const explicitMatch = cleanVal.match(/(\d+(?:\.\d+)?)\s*kw\s*(dc|ac)/i);
    if (explicitMatch) {
      const power = parseFloat(explicitMatch[1]);
      if (power > 0 && power <= 500) return power;
    }
    
    // ENHANCED: Try connector name patterns (like "CCS 150kW", "Type2 22kW", "Type 2 230kW")
    const connectorMatch = cleanVal.match(/(?:ccs|chademo|type\s*[12]|combo|mennekes|generic).*?(\d+(?:\.\d+)?)\s*kw/i);
    if (connectorMatch) {
      const power = parseFloat(connectorMatch[1]);
      if (power > 0 && power <= 500) return power;
    }
    
    // ENHANCED: Try any high power number pattern (for cases like "230kW DC" without connector prefix)
    const highPowerMatch = cleanVal.match(/(\d{2,3}(?:\.\d+)?)\s*kw/i);
    if (highPowerMatch) {
      const power = parseFloat(highPowerMatch[1]);
      if (power >= 20 && power <= 500) return power; // Only accept reasonable high power values
    }
    
    // Try to match decimal numbers followed by optional kW/kw/KW
    const match = cleanVal.match(/(\d+(?:\.\d+)?)\s*(?:kW|kw|KW|kilowatt|kilowatts)?/i);
    if (match) {
      const power = parseFloat(match[1]);
      if (power > 0 && power <= 500) return power; // Reasonable power range
    }
    
    // Try to match just numbers at the beginning
    const numberMatch = cleanVal.match(/^(\d+(?:\.\d+)?)/);
    if (numberMatch) {
      const power = parseFloat(numberMatch[1]);
      if (power > 0 && power <= 500) return power;
    }
    
  }
  
  // Fallback: extract from charger name
  if (chargerObj && typeof chargerObj.name === 'string') {
    const nameMatch = chargerObj.name.match(/([0-9]+\.?[0-9]*)\s*(kW|kw|KW)/);
    if (nameMatch) {
      const power = parseFloat(nameMatch[1]);
      if (power > 0 && power <= 500) return power;
    }
  }
  
  return null;
};

/**
 * Enhanced power formatting with dynamic AC/DC detection
 * Determines AC/DC based on comprehensive connector type analysis and power patterns
 */
export const formatPowerDisplay = (powerVal, connectorType = '', chargerName = '') => {
  if (!powerVal || powerVal <= 0) return 'N/A';
  
  const type = (connectorType || '').toLowerCase();
  const name = (chargerName || '').toLowerCase();
  
  // DC Connector Types (High confidence)
  const dcConnectors = [
    'ccs', 'ccs type 1', 'ccs type 2', 'ccs1', 'ccs2', 'combo',
    'chademo', 'chadimo', 'cha de mo',
    'dc', 'dc fast', 'dcfc', 'direct current',
    'supercharger', 'tesla supercharger'
  ];
  
  // AC Connector Types (High confidence)  
  const acConnectors = [
    'type 1', 'type1', 'j1772', 'sae j1772',
    'type 2', 'type2', 'mennekes', 'iec 62196',
    'schuko', 'cee', 'domestic', '3 pin', '3-pin',
    'nema', 'wall outlet', 'household'
  ];
  
  // Check for explicit DC indicators
  const isDC = dcConnectors.some(dc => type.includes(dc)) ||
              name.includes('dc') ||
              type.includes('dc') ||
              name.includes('fast') ||
              name.includes('rapid') ||
              name.includes('ultra') ||
              name.includes('supercharger') ||
              powerVal >= 50; // High power usually indicates DC
  
  // Check for explicit AC indicators (but exclude high power cases)
  const isAC = (acConnectors.some(ac => type.includes(ac)) ||
               name.includes('ac ') ||
               name.includes(' ac') ||
               type.includes(' ac') ||
               name.includes('slow')) &&
               powerVal < 50; // Don't classify high power as AC even if connector suggests it
  
  // Power-based fallback detection - POWER TAKES PRIORITY
  let currentType;
  
  // HIGH PRIORITY: Power level overrides connector type for obvious cases
  if (powerVal >= 100) {
    currentType = 'DC'; // Very high power is always DC
  } else if (powerVal >= 50 && !isAC) {
    currentType = 'DC'; // High power defaults to DC unless explicitly AC
  } else if (powerVal <= 7.4 && !isDC) {
    currentType = 'AC'; // Very low power defaults to AC unless explicitly DC
  } else if (isDC && !isAC) {
    currentType = 'DC';
  } else if (isAC && !isDC && powerVal < 50) {
    currentType = 'AC'; // Only AC if power is reasonable for AC
  } else {
    // Ambiguous case - use power as primary indicator
    if (powerVal >= 30) {
      currentType = 'DC'; // Medium-high power leans DC
    } else if (powerVal <= 22) {
      currentType = 'AC'; // Medium-low power leans AC
    } else {
      // Final connector-type based decision for 22-30kW range
      if (type.includes('ccs') || type.includes('chademo') || type.includes('combo')) {
        currentType = 'DC';
      } else if (type.includes('type') || type.includes('mennekes') || type.includes('schuko')) {
        currentType = 'AC';
      } else {
        currentType = 'DC'; // Default to DC for ambiguous medium power
      }
    }
  }
  
  
  return `${powerVal}kW ${currentType}`;
};

/**
 * Get power tier color based on power level (webapp style)
 */
export const getPowerTierColor = (power) => {
  const powerNum = typeof power === 'string' ? parseFloat(power) : power;
  
  if (powerNum >= 50) {
    return '#059669'; // Ultra Rapid - Green
  } else if (powerNum >= 22) {
    return '#dc2626'; // Rapid - Red  
  } else if (powerNum >= 7) {
    return '#d97706'; // Fast - Orange
  } else {
    return '#6b7280'; // Slow - Gray
  }
};

/**
 * Get speed label based on power level (webapp style)
 */
export const getSpeedLabel = (power) => {
  const powerNum = typeof power === 'string' ? parseFloat(power) : power;
  
  if (powerNum >= 50) {
    return 'Ultra Rapid';
  } else if (powerNum >= 22) {
    return 'Rapid';
  } else if (powerNum >= 7) {
    return 'Fast';
  } else {
    return 'Slow';
  }
};

/**
 * Enhanced power extraction from connector data (like webapp)
 * Tries multiple power fields and formats the result with AC/DC
 */
// Normalize a raw connector or webapp station into a unified shape
export const normalizeConnector = (raw, index = 0) => {
  if (!raw) return { raw, isStation: false };
  const isStation = (
    (raw.kilowatts !== undefined && raw.kilowatts !== null && (raw.connectorName === 'SOCKET' || raw.connectorType === 'SOCKET')) ||
    (raw.kilowatts !== undefined && raw.stations === undefined && (raw.vendorId || raw.stationId || raw.id) && raw.isAvailable !== undefined && raw.status !== undefined)
  );

  // Original candidate type (do NOT auto-upgrade SOCKET to Type 2 anymore)
  let originalType = raw.connectorType || raw.connectorName || raw.type || raw.name || raw.displayName || raw.socketType || raw.connector;

  const lowerOriginal = (originalType || '').toLowerCase();
  const looksGeneric = !originalType || ['socket','undefined','connector','port'].includes(lowerOriginal);

  // Identify 3 PIN early
  const rawString = JSON.stringify(raw).toLowerCase();
  const isThreePin = /3\s*pin|three\s*pin|domestic/.test(lowerOriginal) || /3\s*pin|domestic/.test(rawString);

  if (looksGeneric) {
    if (isThreePin) {
      originalType = '3 PIN';
    } else if (/ac socket|ac_socket/.test(rawString)) {
      originalType = 'AC Socket';
    } else if (/type ?2|mennekes/.test(rawString)) {
      originalType = 'Type 2';
    } else {
      // Leave as AC Socket if clearly low power (<7kW), else keep Unknown until canonical mapping
      const kwNum = parseFloat(raw.kilowatts || raw.power || '0');
      originalType = kwNum && kwNum < 7 ? '3 PIN' : 'AC Socket';
    }
  }

  // Strip trailing parenthetical power info
  let baseTypeRaw = (originalType || '').replace(/\s*\([^)]*\)/,'').trim();

  // Canonical map (reduced – keeping AC Socket & 3 PIN distinct)
  const CANONICAL_MAP = {
    'type2': 'Type 2','type 2':'Type 2','t2':'Type 2','mennekes':'Type 2','type2ac':'Type 2','type2dc':'Type 2',
    'ccs2':'CCS Type 2','ccs type2':'CCS Type 2','ccs type 2':'CCS Type 2','ccs':'CCS Type 2','combo2':'CCS Type 2','combo 2':'CCS Type 2',
    'chademo':'CHAdeMO','cha de mo':'CHAdeMO','cha de-mo':'CHAdeMO',
    'gb/t':'GB/T','gbt':'GB/T','g b t':'GB/T',
    'bharat dc001':'Bharat DC001','dc001':'Bharat DC001','bharat':'Bharat DC001',
    '3pin':'3 PIN','3 pin':'3 PIN','three pin':'3 PIN','domestic':'3 PIN',
    'ac socket':'AC Socket','acsocket':'AC Socket','ac_outlet':'AC Socket','wall':'AC Socket','wall socket':'AC Socket'
  };

  const key = baseTypeRaw.toLowerCase();
  const canonicalType = CANONICAL_MAP[key] || baseTypeRaw || (isStation ? 'AC Socket' : 'Unknown');

  // Unique identity key to avoid double counting same physical connector/station
  let uniqueKeyParts = [raw.stationId, raw.vendorId, raw.connectorId, raw.id].filter(Boolean);
  if (uniqueKeyParts.length === 0) {
    // fallback: cleaned label + possible coordinates if present to stabilize
    const labelKey = (baseTypeRaw || '').toLowerCase();
    const coordKey = [raw.lat || raw.latitude, raw.lng || raw.longitude].filter(Boolean).join(',');
    uniqueKeyParts = [labelKey, coordKey].filter(Boolean);
  }
  const uniqueKey = uniqueKeyParts.join('|');

  return {
    raw,
    isStation,
    id: raw.connectorId || raw.vendorId || raw.stationId || raw.id || index + 1,
    uniqueKey,
    kilowatts: raw.kilowatts,
    power: raw.power,
    status: raw.status || raw.availability,
    isAvailable: typeof raw.isAvailable === 'boolean' ? raw.isAvailable : undefined,
    baseType: canonicalType,
    displayType: canonicalType
  };
};

export const extractConnectorPower = (connector, charger = null) => {

  // If this looks like a normalized station / object with kilowatts, short‑circuit
  if (connector && connector.kilowatts && (typeof connector.kilowatts === 'string' || typeof connector.kilowatts === 'number')) {
    const pure = parseFloat(connector.kilowatts);
    if (!isNaN(pure) && pure > 0) {
      const formatted = formatPowerDisplay(pure, connector.connectorType || connector.connectorName || connector.type || connector.baseType || '', charger?.name || '');
      return formatted;
    }
  }
  
  // Prioritize webapp data if available
  const effectiveCharger = charger?.webappData || charger;
  
  // Try multiple power field variations (matching webapp priority)
  const powerFields = [
    // Priority 1: Webapp kilowatts field
    connector.kilowatts,
    connector.power,
    connector.maxPower,
    connector.kw,
    connector.powerRating, 
    connector.maxPowerKw,
    connector.maxPowerKW,
    connector.powerKw,
    connector.ratedPower,
    connector.nominalPower,
    connector.maximumPower,
    connector.capacity,
    connector.rating,
    connector.wattage,
    connector.output_power,
    connector.charging_power,
    connector.max_power,
    // CMS-specific fields
    connector.connector_power,
    connector.connector_kw,
    connector.connector_rating,
    // DC-specific power fields
    connector.dcPower,
    connector.dcMaxPower,
    connector.fastChargePower,
    connector.rapidPower,
    connector.maxDcPower,
    connector.dc_power,
    connector.fast_charge_power,
    // AC-specific power fields
    connector.acPower,
    connector.acMaxPower,
    connector.ac_power,
    connector.ac_max_power,
    // Parse power from connector type/name strings
    connector.connectorType,
    connector.connectorName,
    connector.type,
    connector.name
  ];
  
  let powerVal = null;
  let powerSource = 'unknown';
  
  // 1. Extract power from connector fields
  for (const powerField of powerFields) {
    if (powerField !== undefined && powerField !== null && powerField !== '') {
      if (powerField === 0 || powerField === '0' || powerField === '0.0') continue; // skip zero values
      
      // Special debugging for kilowatts field
      if (powerField === connector.kilowatts && connector.kilowatts) {
      }
      
      powerVal = extractKW(powerField, effectiveCharger);
      if (powerVal) {
        powerSource = charger?.webappData ? 'webapp_connector_field' : 'connector_field';
        break;
      }
    }
  }
  
  // 2. Try to extract from connector type or name (like webapp)
  if (!powerVal) {
    // Handle CMS-specific field mapping - try multiple connector name fields
    const connectorFields = [
      connector.connectorType,
      connector.connectorName, 
      connector.type,
      connector.connector,
      connector.socketType,
      connector.name,
      connector.displayName,
      connector.label
    ];
    
    for (const field of connectorFields) {
      if (field && field !== 'undefined' && field !== 'SOCKET' && field !== 'Unknown') {
        
        // Direct power extraction from field
        powerVal = extractKW(field, charger);
        if (powerVal) {
          powerSource = 'connector_field_name';
          break;
        }
      }
    }
  }
  
  // 3. Check if charger has devices.stations structure (like CMS API)
  if (!powerVal && effectiveCharger?.devices && Array.isArray(effectiveCharger.devices)) {
    for (const device of effectiveCharger.devices) {
      if (device.stations && Array.isArray(device.stations)) {
        for (const station of device.stations) {
          const stationPowerFields = [
            station.power, station.kilowatts, station.maxPower, station.kw,
            station.powerRating, station.capacity, station.output_power
          ];
          
          for (const field of stationPowerFields) {
            if (field && field !== 0 && field !== '0' && field !== '0.0') {
              powerVal = extractKW(field, effectiveCharger);
              if (powerVal) {
                powerSource = charger?.webappData ? 'webapp_device_station' : 'device_station';
                break;
              }
            }
          }
          if (powerVal) break;
        }
      }
      if (powerVal) break;
    }
  }
  
  // 4. Fallback: try to extract from charger level fields
  if (!powerVal && effectiveCharger) {
    const chargerPowerFields = [
      effectiveCharger.power, effectiveCharger.kilowatts, effectiveCharger.maxPower, effectiveCharger.kw,
      effectiveCharger.powerRating, effectiveCharger.maxPowerKw, effectiveCharger.capacity, effectiveCharger.rating,
      // CMS-specific charger fields
      effectiveCharger.charger_power, effectiveCharger.charger_kw, effectiveCharger.charger_rating,
      effectiveCharger.max_power_kw, effectiveCharger.nominal_power, effectiveCharger.rated_power
    ];
    
    for (const field of chargerPowerFields) {
      if (field !== undefined && field !== null && field !== '') {
        if (field === 0 || field === '0' || field === '0.0') continue;
        powerVal = extractKW(field, effectiveCharger);
        if (powerVal) {
          powerSource = charger?.webappData ? 'webapp_charger_field' : 'charger_field';
          break;
        }
      }
    }
  }
  
  // 4.5. Check if power info is in connector object but with different field names
  if (!powerVal) {
    
    // Sometimes CMS APIs use these patterns
    const cmsSpecificFields = [
      `connector_${connector.connectorId}_power`,
      `connector_${connector.connectorId}_kw`,
      `socket_${connector.connectorId}_power`,
      `port_${connector.connectorId}_power`
    ];
    
    if (charger) {
      for (const field of cmsSpecificFields) {
        if (charger[field]) {
          powerVal = extractKW(charger[field], charger);
          if (powerVal) {
            powerSource = 'cms_specific_field';
            break;
          }
        }
      }
    }
  }
  
  // 5. Final fallback: try to extract from charger name
  if (!powerVal && effectiveCharger?.name) {
    powerVal = extractKW(effectiveCharger.name, effectiveCharger);
    if (powerVal) {
      powerSource = charger?.webappData ? 'webapp_charger_name' : 'charger_name';
    }
  }
  
  // 6. Intelligent defaults for common connector types when no power data exists
  if (!powerVal) {
    // 6a. Try to compute from voltage * amperage if available (legacy style)
    const voltage = connector.maxVoltage || connector.voltage || connector.max_voltage || connector.volt;
    const amperage = connector.maxAmperage || connector.amperage || connector.max_amperage || connector.amp || connector.current;
    if (!powerVal && voltage && amperage) {
      const computedKW = (parseFloat(voltage) * parseFloat(amperage)) / 1000; // kW approximation
      if (computedKW > 0 && computedKW <= 500) {
        powerVal = parseFloat(computedKW.toFixed(1));
        powerSource = 'computed_voltage_amperage';
      }
    }
    const connectorType = (connector.connectorType !== 'undefined' ? connector.connectorType : 
                          connector.connectorName !== 'SOCKET' ? connector.connectorName : 
                          connector.type || '').toLowerCase();
    
    const defaultPowerMap = {
      'ccs': 50,
      'ccs type 2': 50,
      'ccs type 1': 50,
      'combo': 50,
      'chademo': 50,
      'dc': 50,
      'type 2': 22,
      'type2': 22,
      'mennekes': 22,
      'type 1': 7.4,
      'type1': 7.4,
      'j1772': 7.4,
      'schuko': 3.7,
      '3 pin': 3.7,
      'domestic': 3.7,
      'socket': 7.4, // CMS default for generic socket
      'wall': 3.7
    };
    
    for (const [typeKey, defaultPower] of Object.entries(defaultPowerMap)) {
      if (connectorType.includes(typeKey)) {
        powerVal = defaultPower;
        powerSource = 'intelligent_default';
        break;
      }
    }
  }
  
  // Format with AC/DC designation
  if (powerVal) {
    const connectorType = connector.connectorType || connector.connectorName || connector.type || '';
    const chargerName = charger?.name || '';
    const result = formatPowerDisplay(powerVal, connectorType, chargerName);
    return result;
  }
  
  return 'N/A';
};

// Structured detailed extraction for UI badges
export const extractConnectorPowerDetailed = (connector, charger = null) => {
  const formatted = extractConnectorPower(connector, charger);
  let value = null;
  if (formatted && formatted !== 'N/A') {
    const match = formatted.match(/([0-9]+(?:\.[0-9]+)?)\s*kW/i);
    if (match) value = parseFloat(match[1]);
  }
  return { display: formatted, value };
};

/**
 * Get power display info including color and label
 */
export const getPowerDisplayInfo = (power) => {
  const powerNum = typeof power === 'string' ? parseFloat(power) : power;
  
  return {
    value: powerNum,
    display: power,
    color: getPowerTierColor(powerNum),
    speedLabel: getSpeedLabel(powerNum),
    tier: powerNum >= 50 ? 'ultra-rapid' : 
          powerNum >= 22 ? 'rapid' : 
          powerNum >= 7 ? 'fast' : 'slow'
  };
};

/**
 * Debug function to analyze charger data structure for power extraction
 */
export const debugChargerPowerData = (charger) => {
  
  const powerFields = ['power', 'kilowatts', 'maxPower', 'kw', 'powerRating', 'maxPowerKw', 'capacity'];
  const foundPowerFields = {};
  
  powerFields.forEach(field => {
    if (charger?.[field] !== undefined) {
      foundPowerFields[field] = charger[field];
    }
  });
  
  
  if (charger?.connectors?.length) {
    charger.connectors.forEach((conn, idx) => {
    });
  }
  
  if (charger?.devices?.length) {
    charger.devices.forEach((device, idx) => {
      
      if (device.stations?.length) {
        device.stations.forEach((station, stationIdx) => {
        });
      }
    });
  }
  
  return foundPowerFields;
};